if (!customElements.get('sticky-cart')) {
  customElements.define(
    'sticky-cart',
    class StickyCart extends HTMLElement {
      constructor() {
        super();
        this.stickyCart = null;
        this.originalForm = null;
        this.originalButton = null;
        this.observer = null;
        this.variantSelect = null;
        this.quantityInput = null;
        this.addButton = null;
        this.selectedVariantDisplay = null;
        this.variantList = null;
        this.variantSelector = null;
        this.productForm = null;
        this.isUpdatingVariant = false; // Flag para prevenir loops infinitos
        this.currentVariantId = null; // Variante atual selecionada
        
        this.init();
      }

      init() {
        // Aguardar o DOM estar pronto
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
          this.setup();
        }
      }

      setup() {
        const sectionId = this.getAttribute('data-section-id');
        if (!sectionId) return;

        this.stickyCart = document.getElementById(`stickyCart-${sectionId}`);
        if (!this.stickyCart) return;

        // Encontrar o formulário original e botão
        this.originalForm = document.querySelector(`#ProductInfo-${sectionId} product-form`);
        if (!this.originalForm) return;

        // Tentar encontrar o botão principal de várias formas
        this.originalButton = this.originalForm.querySelector('[type="submit"]') 
          || document.getElementById(`ProductSubmitButton-${sectionId}`)
          || document.querySelector(`[data-adev-add-to-cart]`);
        
        if (!this.originalButton) return;

        // Elementos do sticky cart
        this.quantityInput = document.getElementById(`stickyQuantity-${sectionId}`);
        this.addButton = document.getElementById(`stickyAddToCart-${sectionId}`);
        this.selectedVariantDisplay = document.getElementById(`stickySelectedVariant-${sectionId}`);
        this.variantList = document.getElementById(`stickyVariantList-${sectionId}`);
        this.variantSelector = this.stickyCart.querySelector('.sticky-cart__variant-selector');
        this.productForm = document.getElementById(`stickyCartForm-${sectionId}`);
        this.originalVariantInput = this.originalForm?.querySelector('input[name="id"]');
        this.productImage = this.stickyCart.querySelector('.sticky-cart__img');

        // Configurar observador de visibilidade
        this.setupIntersectionObserver();

        // Configurar eventos
        this.setupEvents();

        // Sincronizar com o formulário original
        this.syncWithOriginalForm();
        
        // Sincronizar estado inicial do botão
        setTimeout(() => {
          this.syncButtonState();
          // Atualizar imagem inicial
          if (this.originalVariantInput && this.originalVariantInput.value) {
            this.updateImage(this.originalVariantInput.value);
          }
        }, 200);
      }

      setupIntersectionObserver() {
        const options = {
          root: null,
          rootMargin: '0px',
          threshold: [0, 1]
        };

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Botão está visível na viewport, esconder sticky cart
              this.hideStickyCart();
            } else {
              // Botão não está visível, verificar se o usuário está abaixo do botão
              this.checkIfShouldShowStickyCart();
            }
          });
        }, options);

        // Adicionar listener de scroll para verificar continuamente quando o botão não está visível
        this.scrollHandler = () => {
          if (this.originalButton) {
            const buttonRect = this.originalButton.getBoundingClientRect();
            // Só verificar se o botão não está visível na viewport
            if (buttonRect.bottom < 0 || buttonRect.top > window.innerHeight) {
              this.checkIfShouldShowStickyCart();
            }
          }
        };
        window.addEventListener('scroll', this.scrollHandler, { passive: true });

        if (this.originalButton) {
          this.observer.observe(this.originalButton);
        }
      }

      checkIfShouldShowStickyCart() {
        if (!this.originalButton) return;

        const buttonRect = this.originalButton.getBoundingClientRect();
        const currentScroll = window.scrollY || window.pageYOffset;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        
        // Calcular a posição absoluta do botão no documento
        // getBoundingClientRect() retorna coordenadas relativas à viewport
        const buttonBottomAbsolute = buttonRect.bottom + currentScroll;
        
        // Verificar se o botão está completamente acima da viewport (usuário já passou dele)
        // Se buttonRect.bottom < 0, significa que toda a parte inferior do botão está acima da viewport
        const isButtonAboveViewport = buttonRect.bottom < 0;
        
        // Verificar se o usuário está em uma seção abaixo do botão
        // O scroll atual + altura da viewport deve estar além da posição absoluta inferior do botão
        const scrollBottom = currentScroll + viewportHeight;
        const isUserBelowButton = scrollBottom > buttonBottomAbsolute;

        // Mostrar sticky cart apenas se:
        // 1. O botão está completamente acima da viewport (usuário já passou dele no scroll)
        // 2. O usuário está em uma seção abaixo do botão (já passou da parte inferior do botão no documento)
        if (isButtonAboveViewport && isUserBelowButton) {
          this.showStickyCart();
        } else {
          this.hideStickyCart();
        }
      }

      showStickyCart() {
        if (this.stickyCart && !this.stickyCart.classList.contains('is-visible')) {
          this.stickyCart.style.display = 'block';
          // Pequeno delay para permitir o display antes da animação
          setTimeout(() => {
            this.stickyCart.classList.add('is-visible');
          }, 10);
        }
      }

      hideStickyCart() {
        if (this.stickyCart && this.stickyCart.classList.contains('is-visible')) {
          this.stickyCart.classList.remove('is-visible');
          // Aguardar animação antes de esconder
          setTimeout(() => {
            if (!this.stickyCart.classList.contains('is-visible')) {
              this.stickyCart.style.display = 'none';
            }
          }, 300);
        }
      }

      setupEvents() {
        // Seleção de variante
        if (this.variantSelector) {
          const selectedVariant = this.variantSelector.querySelector('.sticky-cart__selected-variant');
          if (selectedVariant) {
            selectedVariant.addEventListener('click', () => {
              this.variantSelector.classList.toggle('is-open');
            });
          }
        }

        // Clicar em uma opção de variante
        if (this.variantList) {
          this.variantList.addEventListener('click', (e) => {
            const option = e.target.closest('.sticky-cart__variant-option');
            if (!option) return;

            const variantId = option.getAttribute('data-variant-id');
            const variantTitle = option.getAttribute('data-variant-title');
            const variantPrice = option.getAttribute('data-variant-price');

            // Atualizar imagem imediatamente (antes de fechar o dropdown)
            this.updateImage(variantId);
            
            // Selecionar variante
            this.selectVariant(variantId, variantTitle, variantPrice, option);
            
            // Fechar dropdown
            if (this.variantSelector) {
              this.variantSelector.classList.remove('is-open');
            }
          });
        }

        // Fechar dropdown ao clicar fora
        document.addEventListener('click', (e) => {
          if (this.variantSelector && !this.variantSelector.contains(e.target)) {
            this.variantSelector.classList.remove('is-open');
          }
        });

        // Quantidade
        if (this.quantityInput) {
          const qtyButtons = this.stickyCart.querySelectorAll('[data-qty-action]');
          qtyButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
              const action = e.currentTarget.getAttribute('data-qty-action');
              const currentQty = parseInt(this.quantityInput.value) || 1;
              
              if (action === 'increase') {
                this.quantityInput.value = currentQty + 1;
              } else if (action === 'decrease' && currentQty > 1) {
                this.quantityInput.value = currentQty - 1;
              }

              this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
            });
          });
        }

        // Submit do formulário
        if (this.productForm) {
          this.productForm.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleSubmit();
          });
        }

        // Escutar mudanças de variante no formulário original
        this.listenToVariantChanges();
      }

      selectVariant(variantId, variantTitle, variantPrice, optionElement) {
        if (!this.selectedVariantDisplay) return;

        // Prevenir loops infinitos: se já está atualizando ou a variante é a mesma, retornar
        if (this.isUpdatingVariant || this.currentVariantId === variantId.toString()) {
          return;
        }

        this.isUpdatingVariant = true;
        this.currentVariantId = variantId.toString();

        // Atualizar variante no formulário original
        if (this.originalVariantInput && this.originalVariantInput.value !== variantId.toString()) {
          this.originalVariantInput.value = variantId;
          this.originalVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Atualizar display com estrutura de spans para manter o preço na mesma linha
        const price = this.formatMoney(parseInt(variantPrice));
        
        // Verificar se já existe a estrutura de spans
        let titleSpan = this.selectedVariantDisplay.querySelector('.sticky-cart__variant-title');
        let priceWrapper = this.selectedVariantDisplay.querySelector('.sticky-cart__variant-price-wrapper');
        let priceSpan = priceWrapper ? priceWrapper.querySelector('.sticky-cart__variant-price') : null;
        
        if (!titleSpan || !priceWrapper || !priceSpan) {
          // Criar estrutura de spans se não existir (com wrapper para manter preço junto)
          this.selectedVariantDisplay.innerHTML = `
            <span class="sticky-cart__variant-title">${variantTitle}</span>
            <span class="sticky-cart__variant-price-wrapper">
              <span class="sticky-cart__variant-price">${price}</span>
            </span>
          `;
        } else {
          // Atualizar apenas o conteúdo
          titleSpan.textContent = variantTitle;
          priceSpan.textContent = price;
        }

        // Atualizar classes das opções
        if (this.variantList) {
          this.variantList.querySelectorAll('.sticky-cart__variant-option').forEach(opt => {
            opt.classList.remove('is-selected');
          });
          if (optionElement) {
            optionElement.classList.add('is-selected');
          }
        }

        // Sincronizar botão com o botão principal após um pequeno delay
        // para garantir que o botão principal foi atualizado primeiro
        setTimeout(() => {
          this.syncButtonState();
        }, 150);

        // Atualizar imagem se necessário
        this.updateImage(variantId);

        // Liberar flag após um pequeno delay para permitir que outros eventos processem
        setTimeout(() => {
          this.isUpdatingVariant = false;
        }, 100);
      }
      
      getProductData() {
        // Buscar dados do produto do JSON do tema
        const productJson = document.querySelector('[data-adev-product-json]');
        if (productJson) {
          try {
            return JSON.parse(productJson.textContent);
          } catch (e) {
            console.error('Error parsing product JSON:', e);
          }
        }
        return null;
      }

      updateImage(variantId) {
        // Buscar imagem da variante - primeiro tenta buscar da galeria principal, depois dos atributos data
        if (!variantId || !this.stickyCart) return;

        const productImage = this.stickyCart.querySelector('.sticky-cart__img');
        if (!productImage) return;

        let imageUrl = null;
        let imageAlt = '';

        // Prioridade 1: Buscar do atributo data-variant-image (mais confiável para variantes específicas)
        const variantOption = this.variantList?.querySelector(`[data-variant-id="${variantId}"]`);
        if (variantOption) {
          imageUrl = variantOption.getAttribute('data-variant-image');
          imageAlt = variantOption.getAttribute('data-variant-image-alt') || variantOption.getAttribute('data-variant-title') || '';
          
          // Converter URL relativa para absoluta se necessário
          if (imageUrl && imageUrl.startsWith('/')) {
            imageUrl = window.location.origin + imageUrl;
          }
        }

        // Prioridade 2: Tentar buscar da galeria principal (imagem ativa exibida)
        if (!imageUrl) {
          const activeMediaItem = document.querySelector('.product__media-item.is-active img, .adev-product-single__media img[style*="opacity: 1"]');
          if (activeMediaItem) {
            const srcValue = activeMediaItem.src || activeMediaItem.getAttribute('src');
            if (srcValue) {
              // Converter para tamanho menor (120px)
              imageUrl = srcValue.replace(/_\d+x\d+/g, '_120x120').replace(/(\?.*)?$/, '');
              imageAlt = activeMediaItem.alt || '';
            }
          }
        }

        // Prioridade 3: Buscar do produto JSON
        if (!imageUrl) {
          const productData = this.getProductData();
          if (productData && productData.variants) {
            const variant = productData.variants.find(v => v.id.toString() === variantId.toString());
            if (variant) {
              if (variant.featured_image) {
                imageUrl = variant.featured_image.src;
                imageAlt = variant.featured_image.alt || '';
              } else if (variant.image) {
                imageUrl = typeof variant.image === 'string' ? variant.image : variant.image.src;
                imageAlt = variant.title || '';
              }
              
              // Redimensionar URL se necessário
              if (imageUrl && !imageUrl.includes('_120x')) {
                imageUrl = imageUrl.replace(/(\?.*)?$/, '') + '_120x120' + (imageUrl.match(/\?.*/) ? imageUrl.match(/\?.*/)[0] : '');
              }
            }
          }
        }

        // Atualizar imagem
        if (imageUrl) {
          // Forçar atualização mesmo se a URL for a mesma (para garantir que a imagem seja recarregada)
          const currentSrc = productImage.src;
          if (currentSrc === imageUrl || currentSrc.endsWith(imageUrl.split('/').pop())) {
            // Se a URL é a mesma, forçar recarregamento adicionando timestamp
            productImage.src = imageUrl + (imageUrl.includes('?') ? '&' : '?') + '_t=' + Date.now();
          } else {
            productImage.src = imageUrl;
          }
          
          if (imageAlt) {
            productImage.alt = imageAlt;
          }
          
          // Efeito de transição suave
          productImage.style.opacity = '0.6';
          productImage.style.transition = 'opacity 0.3s ease-in-out';
          
          productImage.onload = () => {
            productImage.style.opacity = '1';
          };
          
          // Fallback caso a imagem já esteja em cache
          if (productImage.complete) {
            setTimeout(() => {
              productImage.style.opacity = '1';
            }, 50);
          }
        }
      }

      syncWithOriginalForm() {
        // Prevenir loops infinitos
        if (this.isUpdatingVariant) return;

        // Sincronizar variante selecionada
        if (this.originalVariantInput) {
          const currentVariantId = this.originalVariantInput.value;
          if (currentVariantId && this.currentVariantId !== currentVariantId.toString()) {
            const variantOption = this.variantList?.querySelector(`[data-variant-id="${currentVariantId}"]`);
            if (variantOption) {
              const variantTitle = variantOption.getAttribute('data-variant-title');
              const variantPrice = variantOption.getAttribute('data-variant-price');
              this.selectVariant(currentVariantId, variantTitle, variantPrice, variantOption);
            }
          }
        }
        
        // Sincronizar estado do botão após sincronizar variante
        setTimeout(() => {
          this.syncButtonState();
        }, 150);
      }

      listenToVariantChanges() {
        // Escutar mudanças no formulário original usando eventos customizados
        // Usar uma função nomeada para poder remover o listener depois
        this.variantChangeHandler = (e) => {
          if (this.isUpdatingVariant) return; // Prevenir loops
          if (e.detail?.variantId) {
            const variantId = e.detail.variantId.toString();
            if (this.currentVariantId !== variantId) {
              this.updateVariantFromId(variantId);
            }
          }
          // Sincronizar estado do botão após mudança de variante
          setTimeout(() => {
            this.syncButtonState();
          }, 150);
        };
        document.addEventListener('variant:change', this.variantChangeHandler);
        
        // Observar mudanças no botão principal para sincronizar em tempo real
        if (this.originalButton) {
          this.buttonObserver = new MutationObserver(() => {
            if (!this.isUpdatingVariant) {
              this.syncButtonState();
            }
          });

          // Observar mudanças em atributos e conteúdo do botão
          this.buttonObserver.observe(this.originalButton, {
            attributes: true,
            attributeFilter: ['disabled', 'class'],
            childList: true,
            subtree: true,
            characterData: true
          });
        }

          // Escutar mudanças via MutationObserver no input hidden de variante
        if (this.originalVariantInput) {
          this.variantObserver = new MutationObserver(() => {
            // Prevenir loops infinitos
            if (this.isUpdatingVariant) return;

            const variantId = this.originalVariantInput.value;
            if (variantId && this.currentVariantId !== variantId.toString()) {
              this.updateVariantFromId(variantId);
            }
          });

          this.variantObserver.observe(this.originalVariantInput, {
            attributes: true,
            attributeFilter: ['value']
          });

          // Também escutar mudanças diretas (com debounce para prevenir loops)
          this.originalVariantInput.addEventListener('change', () => {
            // Usar setTimeout para evitar chamadas síncronas em loop
            setTimeout(() => {
              if (!this.isUpdatingVariant) {
                this.syncWithOriginalForm();
              }
            }, 50);
          });
        }

        // Escutar mudanças no variant-radios (componente de variantes do Dawn)
        const variantRadios = document.querySelector('variant-radios');
        if (variantRadios) {
          variantRadios.addEventListener('change', () => {
            setTimeout(() => this.syncWithOriginalForm(), 100);
          });
        }

        // Escutar eventos de mudança de variante via PubSub (se disponível)
        if (window.subscribe) {
          this.variantChangeUnsubscriber = window.subscribe('variant-change', (event) => {
            if (event.data?.variant?.id) {
              this.updateVariantFromId(event.data.variant.id.toString());
            }
          });
        }
      }

      updateVariantFromId(variantId) {
        // Prevenir loops infinitos
        if (this.isUpdatingVariant || this.currentVariantId === variantId.toString()) {
          return;
        }

        const variantOption = this.variantList?.querySelector(`[data-variant-id="${variantId}"]`);
        if (variantOption) {
          const variantTitle = variantOption.getAttribute('data-variant-title');
          const variantPrice = variantOption.getAttribute('data-variant-price');
          this.selectVariant(variantId, variantTitle, variantPrice, variantOption);
        }
      }

      async handleSubmit() {
        if (!this.productForm || !this.addButton) return;

        // Buscar variant ID do formulário original
        const variantId = this.originalVariantInput?.value;
        const quantity = this.quantityInput?.value || 1;

        if (!variantId) {
          console.error('Variant ID is required');
          return;
        }

        // Desabilitar botão e mostrar loading
        this.addButton.disabled = true;
        this.addButton.classList.add('loading');
        const originalText = this.addButton.querySelector('.sticky-cart__add-text')?.textContent;
        if (this.addButton.querySelector('.sticky-cart__add-text')) {
          this.addButton.querySelector('.sticky-cart__add-text').textContent = 'Adicionando...';
        }

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: parseInt(quantity)
            })
          });

          const data = await response.json();

          if (response.ok) {
            // Sucesso - atualizar carrinho
            if (window.dispatchEvent) {
              window.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
            }
            
            // Trigger para atualizar drawer/cart
            if (window.$) {
              $(document).trigger('adev:cart:drawer:refresh');
              $(document).trigger('adev:open:drawer', 'cart-drawer');
            }

            // Mostrar feedback visual
            this.showSuccessFeedback();
          } else {
            throw new Error(data.message || 'Erro ao adicionar ao carrinho');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          this.showErrorFeedback(error.message);
        } finally {
          // Restaurar botão
          this.addButton.disabled = false;
          this.addButton.classList.remove('loading');
          if (originalText && this.addButton.querySelector('.sticky-cart__add-text')) {
            this.addButton.querySelector('.sticky-cart__add-text').textContent = originalText;
          }
        }
      }

      showSuccessFeedback() {
        // Feedback visual simples
        const originalText = this.addButton.querySelector('.sticky-cart__add-text')?.textContent;
        if (this.addButton.querySelector('.sticky-cart__add-text')) {
          this.addButton.querySelector('.sticky-cart__add-text').textContent = 'Adicionado!';
          setTimeout(() => {
            if (this.addButton.querySelector('.sticky-cart__add-text')) {
              this.addButton.querySelector('.sticky-cart__add-text').textContent = originalText || 'Adicionar ao carrinho';
            }
          }, 2000);
        }
      }

      showErrorFeedback(message) {
        console.error('Add to cart error:', message);
        // Aqui você pode adicionar uma notificação de erro mais elaborada
      }

      formatMoney(cents) {
        // Tentar usar o formato de dinheiro do tema se disponível
        if (window.Shopify && window.Shopify.formatMoney) {
          const moneyFormat = window.theme?.moneyFormat || window.money_format || 'R$ {{amount}}';
          return window.Shopify.formatMoney(cents, moneyFormat);
        }
        
        // Formatação padrão para BRL com símbolo
        const amount = cents / 100;
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        }).format(amount);
      }

      getAddToCartText() {
        return 'Adicionar ao carrinho';
      }

      getSoldOutText() {
        return 'Esgotado';
      }

      syncButtonState() {
        // Sincronizar estado do botão sticky cart com o botão principal
        if (!this.originalButton || !this.addButton) return;

        // Sincronizar disabled/enabled
        this.addButton.disabled = this.originalButton.disabled;

        // Sincronizar texto (remover SVG se houver)
        const originalButtonSpan = this.originalButton.querySelector('span');
        const originalButtonText = originalButtonSpan ? originalButtonSpan.textContent.trim() : this.originalButton.textContent.trim();
        const addText = this.addButton.querySelector('.sticky-cart__add-text');
        
        if (addText && originalButtonText) {
          // Usar apenas o texto, sem SVG
          addText.textContent = originalButtonText;
        }
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }
        if (this.variantObserver) {
          this.variantObserver.disconnect();
        }
        if (this.buttonObserver) {
          this.buttonObserver.disconnect();
        }
        if (this.variantChangeHandler) {
          document.removeEventListener('variant:change', this.variantChangeHandler);
        }
        if (this.scrollHandler) {
          window.removeEventListener('scroll', this.scrollHandler);
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        // Limpar flags
        this.isUpdatingVariant = false;
        this.currentVariantId = null;
      }
    }
  );
}

